import { useRef, useState } from "react";
import {
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
  type DragMoveEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import {
  DRAG_TYPE,
  type AnyDragData,
  type AnyDropData,
  type ImageDragData,
  type RemovedImageDragData,
} from "@/lib/smartupload-dnd";
import { EditableProduct, SmartUploadFormValues } from "@/schema/smartupload.schema";
import { RemovedImage } from "@/store/smartupload.store";
import { UseFormGetValues } from "react-hook-form";

interface UseSmartUploadDndArgs {
  getValues: UseFormGetValues<SmartUploadFormValues>;
  update: (index: number, value: EditableProduct) => void;
  removeImage: (entry: RemovedImage) => void;
  restoreImage: (imageId: string) => RemovedImage | undefined;
}

export function useSmartUploadDnd({
  getValues,
  update,
  removeImage,
  restoreImage,
}: UseSmartUploadDndArgs) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeDrag, setActiveDrag] = useState<AnyDragData | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const collisionDetection = (args: Parameters<typeof closestCenter>[0]) => {
    const variantHits = pointerWithin(args).filter(
      (c) => c.data?.droppableContainer?.data?.current?.type === DRAG_TYPE.VARIANT
    );
    if (variantHits.length > 0) return variantHits;

    const removedHits = pointerWithin(args).filter(
      (c) => c.data?.droppableContainer?.data?.current?.type === DRAG_TYPE.REMOVED_SECTION
    );
    if (removedHits.length > 0) return removedHits;

    return closestCenter(args);
  };

  const handleDragStart = (e: DragStartEvent) => {
    setActiveDrag(e.active.data.current as AnyDragData);
  };

  const handleDragMove = (e: DragMoveEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = (e.activatorEvent as PointerEvent)?.clientY ?? 0;
    const THRESHOLD = 80;
    const SPEED = 12;
    if (y < rect.top + THRESHOLD) el.scrollTop -= SPEED;
    else if (y > rect.bottom - THRESHOLD) el.scrollTop += SPEED;
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveDrag(null);
    if (!over) return;

    const src = active.data.current as AnyDragData;
    const dst = over.data.current as AnyDropData;
    const all = getValues("products");

    // ── Drag removed image → drop on variant (restore to chosen location) ────
    if (src.type === DRAG_TYPE.REMOVED_IMAGE) {
      const removedSrc = src as RemovedImageDragData;
      const entry = restoreImage(removedSrc.imageId);
      if (!entry) return;

      if (dst.type === DRAG_TYPE.VARIANT || dst.type === DRAG_TYPE.IMAGE) {
        const dstProd = all[dst.productIndex];
        const dstVIdx = dstProd.variants.findIndex((v) => v.id === dst.variantId);
        const imgs = [...dstProd.variants[dstVIdx].images];
        if (dst.type === DRAG_TYPE.IMAGE) {
          const at = imgs.findIndex((im) => im.id === String(over.id));
          imgs.splice(at === -1 ? imgs.length : at, 0, entry.image);
        } else {
          imgs.push(entry.image);
        }
        update(dst.productIndex, {
          ...dstProd,
          variants: dstProd.variants.map((v, i) => (i === dstVIdx ? { ...v, images: imgs } : v)),
        });
      }
      return;
    }

    // ── Drag variant image → drop on removed section (remove via drag) ───────
    if (dst.type === DRAG_TYPE.REMOVED_SECTION) {
      const imgSrc = src as ImageDragData;
      const product = all[imgSrc.productIndex];
      const variant = product.variants.find((v) => v.id === imgSrc.variantId)!;
      const img = variant.images[imgSrc.imageIndex];
      update(imgSrc.productIndex, {
        ...product,
        variants: product.variants.map((v) =>
          v.id !== imgSrc.variantId
            ? v
            : { ...v, images: v.images.filter((_, i) => i !== imgSrc.imageIndex) }
        ),
      });
      removeImage({
        image: img,
        productId: product.id,
        productName: product.name,
        variantId: variant.id,
        variantName: variant.name,
        originalIndex: imgSrc.imageIndex,
      });
      return;
    }

    // ── Drag variant image → drop on variant or image ────────────────────────
    if (src.type !== DRAG_TYPE.IMAGE) return;
    if (active.id === over.id) return;
    if (dst.type !== DRAG_TYPE.VARIANT && dst.type !== DRAG_TYPE.IMAGE) return;

    const imgSrc = src as ImageDragData;
    const dstProductIndex = dst.productIndex;
    const dstVariantId = dst.variantId;

    // Same variant → reorder
    if (imgSrc.productIndex === dstProductIndex && imgSrc.variantId === dstVariantId) {
      if (dst.type !== DRAG_TYPE.IMAGE) return;
      const prod = all[imgSrc.productIndex];
      const vIdx = prod.variants.findIndex((v) => v.id === imgSrc.variantId);
      const dstImgIdx = prod.variants[vIdx].images.findIndex((im) => im.id === String(over.id));
      if (dstImgIdx === -1) return;
      const reordered = arrayMove(prod.variants[vIdx].images, imgSrc.imageIndex, dstImgIdx);
      update(imgSrc.productIndex, {
        ...prod,
        variants: prod.variants.map((v, i) => (i === vIdx ? { ...v, images: reordered } : v)),
      });
      return;
    }

    // Cross-variant (possibly cross-product)
    const imageToMove = all[imgSrc.productIndex].variants.find(
      (v) => v.id === imgSrc.variantId
    )!.images[imgSrc.imageIndex];

    const srcVariants = all[imgSrc.productIndex].variants.map((v) =>
      v.id !== imgSrc.variantId
        ? v
        : { ...v, images: v.images.filter((_, i) => i !== imgSrc.imageIndex) }
    );

    const buildDstVariants = (baseProd: EditableProduct) => {
      const dstVIdx = baseProd.variants.findIndex((v) => v.id === dstVariantId);
      const imgs = [...baseProd.variants[dstVIdx].images];
      if (dst.type === DRAG_TYPE.IMAGE) {
        const at = imgs.findIndex((im) => im.id === String(over.id));
        imgs.splice(at === -1 ? imgs.length : at, 0, imageToMove);
      } else {
        imgs.push(imageToMove);
      }
      return baseProd.variants.map((v, i) => (i === dstVIdx ? { ...v, images: imgs } : v));
    };

    if (imgSrc.productIndex === dstProductIndex) {
      const intermediateProd = { ...all[imgSrc.productIndex], variants: srcVariants };
      update(imgSrc.productIndex, {
        ...intermediateProd,
        variants: buildDstVariants(intermediateProd),
      });
    } else {
      update(imgSrc.productIndex, { ...all[imgSrc.productIndex], variants: srcVariants });
      update(dstProductIndex, {
        ...all[dstProductIndex],
        variants: buildDstVariants(all[dstProductIndex]),
      });
    }
  };

  const { setNodeRef: setRemovedRef, isOver: isOverRemoved } = useDroppable({
    id: "removed-section",
    data: { type: DRAG_TYPE.REMOVED_SECTION },
  });

  return {
    scrollRef,
    activeDrag,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    setRemovedRef,
    isOverRemoved,
  };
}
